<script setup>
import { computed, onMounted, ref } from 'vue'
import { Bell, CheckCheck, LoaderCircle } from 'lucide-vue-next'
import Popover from 'primevue/popover'
import { useRouter } from 'vue-router'
import NotificationRow from './NotificationRow.vue'
import { useNotifications } from '@/composables/useNotifications'
import { routeForRouter } from '@/utils/routerRoute'

const router = useRouter()
const open = ref(false)
const popover = ref(null)
const {
  notifications,
  unreadCount,
  loading,
  error,
  ensureLoaded,
  refresh,
  markRead,
  markAllRead,
} = useNotifications()

const recentNotifications = computed(() => notifications.value.slice(0, 8))
const badge = computed(() => (unreadCount.value > 99 ? '99+' : unreadCount.value))

onMounted(() => {
  ensureLoaded().catch(() => {})
})

function togglePopover(event) {
  popover.value?.toggle(event)
}

function onPopoverShow() {
  open.value = true
  refresh(8).catch(() => {})
}

function onPopoverHide() {
  open.value = false
}

async function selectNotification(notification) {
  try {
    await markRead(notification)
  } catch {
    // The row remains usable even if the read request fails.
  }
  popover.value?.hide()
  if (notification.route) router.push(routeForRouter(notification.route, router.options.history.base))
}

async function markAll() {
  try {
    await markAllRead()
  } catch {
    // Keep the dropdown available and let the next open retry.
  }
}

function viewAll() {
  popover.value?.hide()
  router.push({ name: 'notifications' })
}
</script>

<template>
  <div class="notification-bell">
    <button
      type="button"
      class="icon-button notification-bell-button"
      :aria-expanded="open"
      aria-haspopup="dialog"
      :aria-label="$t('notifications.title')"
      @click="togglePopover"
    >
      <Bell
        :size="21"
        :stroke-width="1.8"
      />
      <span
        v-if="unreadCount"
        class="notification-badge"
      >{{ badge }}</span>
    </button>

    <Popover
      ref="popover"
      class="notification-popover"
      append-to="body"
      :base-z-index="60"
      unstyled
      :aria-label="$t('notifications.title')"
      @show="onPopoverShow"
      @hide="onPopoverHide"
    >
      <header class="notification-popover-header">
        <strong>{{ $t('notifications.title') }}</strong>
        <button
          v-if="unreadCount"
          type="button"
          class="text-button"
          @click="markAll"
        >
          <CheckCheck :size="15" />
          {{ $t('notifications.markAllRead') }}
        </button>
      </header>

      <div
        v-if="loading"
        class="notification-state"
      >
        <LoaderCircle
          class="spin"
          :size="18"
        />
        {{ $t('notifications.loading') }}
      </div>
      <div
        v-else-if="error"
        class="notification-state notification-state--error"
      >
        <span>{{ $t('notifications.loadError') }}</span>
        <button
          type="button"
          class="text-button"
          @click="refresh(8)"
        >{{ $t('common.tryAgain') }}</button>
      </div>
      <div
        v-else-if="!recentNotifications.length"
        class="notification-state"
      >{{ $t('notifications.empty') }}</div>
      <div v-else>
        <NotificationRow
          v-for="notification in recentNotifications"
          :key="notification.name"
          :notification="notification"
          compact
          @select="selectNotification"
        />
      </div>

      <button
        type="button"
        class="view-all-button"
        @click="viewAll"
      >{{ $t('notifications.viewAll') }}</button>
    </Popover>
  </div>
</template>

<style scoped>
.notification-bell {
  position: relative;
}

.notification-bell-button {
  position: relative;
  width: 34px;
  height: 34px;
  padding: 0;
  display: grid;
  place-items: center;
  color: #353646;
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
}

.notification-bell-button:hover {
  background: #f3f3f7;
}

.notification-badge {
  position: absolute;
  top: -3px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: grid;
  place-items: center;
  color: #fff;
  background: #d24e68;
  border: 2px solid #fff;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
}

.notification-popover {
  width: min(370px, calc(100vw - 28px));
  overflow: hidden;
  background: var(--surface-card);
  border: 1px solid #dedee8;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgb(44 43 93 / 16%);
}

.notification-popover-header {
  min-height: 48px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #ededf3;
}

.notification-popover-header strong {
  color: #2b2a37;
  font-size: 15px;
}

.text-button,
.view-all-button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #5048ce;
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.notification-state {
  min-height: 74px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #767585;
  font-size: 13px;
}

.notification-state--error {
  flex-direction: column;
  color: #a34e5d;
}

.view-all-button {
  width: 100%;
  min-height: 43px;
  justify-content: center;
  background: #fafaff;
  border-top: 1px solid #ededf3;
  font-weight: 700;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

</style>
